import CountrySelect from './CountrySelect'
import AddressRolesSelect from './AddressRolesSelect'
import { ADDR_CDS_LIMITS, MAX_ADDRESS_LINES, type AddressDraft, type AddressFieldsValue } from '../lib/customerAddress'

// The address input block used by the company page (add / edit), the New Company window and the job page's "+ Create"
// window. Pure input: the caller owns saving (always into customer_addresses).
const inputCls = 'w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500'
const labelCls = 'block text-xs font-medium text-gray-500 mb-1'

interface Props {
  value: AddressFieldsValue
  onChange: (patch: Partial<AddressDraft>) => void
  showRoles?: boolean
  // Other addresses of the same company holding a primary role: role -> short label (so ticking primary can say what it replaces).
  takenPrimary?: Record<string, string>
  showLabel?: boolean
  showContact?: boolean
  showNotes?: boolean
}

export default function CustomerAddressFields({ value, onChange, showRoles, takenPrimary, showLabel, showContact, showNotes }: Props) {
  const lines = value.address_lines && value.address_lines.length > 0 ? value.address_lines : ['']
  const setLine = (i: number, v: string) => onChange({ address_lines: lines.map((l, idx) => (idx === i ? v : l)) })
  return (
    <div className="grid grid-cols-2 gap-3 mb-4">
      {showRoles && (
        <AddressRolesSelect roles={value.roles ?? ['operations']} primaryRoles={value.primary_roles ?? []} taken={takenPrimary}
          onChange={next => onChange(next)} />
      )}
      {showLabel && (
        <div>
          <label className={labelCls}>Label (optional)</label>
          <input type="text" value={value.label ?? ''} onChange={e => onChange({ label: e.target.value })} className={inputCls} placeholder="e.g. Head Office" />
        </div>
      )}
      {/* Dynamic address lines -- no fixed 1/2 split. Each line is capped at 35 chars (CDS STREET) as an input convenience. */}
      <div className="col-span-2">
        <label className={labelCls}>Address Lines</label>
        <div className="space-y-2">
          {lines.map((line, i) => (
            <div key={i} className="flex gap-2">
              <input type="text" value={line} maxLength={ADDR_CDS_LIMITS.addressLine} onChange={e => setLine(i, e.target.value)}
                className={inputCls} placeholder={i === 0 ? 'Street address' : 'Additional address line'} />
              {lines.length > 1 && (
                <button type="button" onClick={() => { const next = lines.filter((_, idx) => idx !== i); onChange({ address_lines: next.length ? next : [''] }) }}
                  className="px-2 text-gray-400 hover:text-red-500" title="Remove line">✕</button>
              )}
            </div>
          ))}
        </div>
        {lines.length < MAX_ADDRESS_LINES && (
          <button type="button" onClick={() => onChange({ address_lines: [...lines, ''] })} className="mt-1 text-xs text-blue-600 hover:underline">+ Add line</button>
        )}
      </div>
      <div>
        <label className={labelCls}>City</label>
        <input type="text" value={value.city ?? ''} onChange={e => onChange({ city: e.target.value })} maxLength={ADDR_CDS_LIMITS.city} className={inputCls} placeholder="Birmingham" />
      </div>
      <div>
        <label className={labelCls}>County</label>
        <input type="text" value={value.county ?? ''} onChange={e => onChange({ county: e.target.value })} className={inputCls} placeholder="West Midlands" />
      </div>
      <div>
        <label className={labelCls}>Postcode</label>
        <input type="text" value={value.postcode ?? ''} onChange={e => onChange({ postcode: e.target.value.toUpperCase() })} maxLength={ADDR_CDS_LIMITS.postcode} className={inputCls} placeholder="B1 1AA" />
      </div>
      <div>
        <label className={labelCls}>Country</label>
        <CountrySelect value={value.country ?? 'GB'} onChange={code => onChange({ country: code })} />
      </div>
      {showContact && (
        <>
          <div className="col-span-2 border-t border-gray-100 dark:border-gray-700 pt-3">
            <p className="text-xs font-medium text-gray-500 mb-2">Site Contact (optional)</p>
          </div>
          <div className="col-span-2">
            <label className={labelCls}>Contact Name</label>
            <input type="text" value={value.contact_name ?? ''} onChange={e => onChange({ contact_name: e.target.value })} className={inputCls} placeholder="John Smith" />
          </div>
          <div>
            <label className={labelCls}>Phone</label>
            <input type="text" value={value.contact_phone ?? ''} onChange={e => onChange({ contact_phone: e.target.value })} className={inputCls} placeholder="+44 121 000 0000" />
          </div>
          <div>
            <label className={labelCls}>Email</label>
            <input type="email" value={value.contact_email ?? ''} onChange={e => onChange({ contact_email: e.target.value })} className={inputCls} placeholder="ops@company.com" />
          </div>
        </>
      )}
      {showNotes && (
        <div className="col-span-2">
          <label className={labelCls}>Notes</label>
          <input type="text" value={value.notes ?? ''} onChange={e => onChange({ notes: e.target.value })} className={inputCls} placeholder="Delivery hours, access instructions…" />
        </div>
      )}
    </div>
  )
}
